import { Component, input, ChangeDetectorRef, inject, InputSignal, model, ModelSignal, OnDestroy, output, OutputEmitterRef, signal } from '@angular/core';

import { ControlValueAccessor, FormsModule } from '@angular/forms';

import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { SplitButtonComponent } from '../split-buttons/split-button.component';
import { SplitButtonsComponent } from '../split-buttons/split-buttons.component';
import { IconFlagComponent } from '../icon/icon-flag.component';
import { SplitButtonDataComponent } from '../split-buttons/split-button-data.component';

/**
 * The Currency Selector Component
 *
 * This is the button for selecting currency.
 */
@Component({
  imports: [
    FormsModule,
    IconFlagComponent,
    SplitButtonsComponent,
    SplitButtonComponent,
    SplitButtonDataComponent
  ],
  selector: 'td-wb-currency-selector',
  templateUrl: './currency-selector.component.html',
  styleUrls: ['./currency-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(CurrencySelectorComponent)],
  exportAs: 'tdWbCurrencySelector'
})
export class CurrencySelectorComponent implements ControlValueAccessor, OnDestroy {
  readonly codes: InputSignal<Array<string>> = input(['CAD', 'USD']);
  readonly size: InputSignal<string> = input('16x11');
  readonly ariaLabel: InputSignal<string | undefined> = input(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input(undefined, { alias: 'aria-labelledby' });
  readonly ariaDescribedby: InputSignal<string | undefined> = input(undefined, { alias: 'aria-describedby' });

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly disabledByCva = signal(false);

  /**
   * Single source of truth, and the Signal Forms native custom-control binding.
   * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly value: ModelSignal<string | null | undefined> = model<string | null | undefined>('CAD');

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  /**
   *  ControlValueAccessor.writeValue — owner -> control, never emits onChangeCallback.
   *
   * @param value
   */
  writeValue(value: string | null | undefined): void {
    this.value.set(value);
    this.setSelectedCurrency();
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  ngOnDestroy(): void {
  }

  setSelectedCurrency(): void {
    const current = this.value();
    this.value.set(current === undefined || this.codes().indexOf(current) < 0 ? 'CAD' : current);
  }

  onCurrencyChange(currency: string): void {
    this.value.set(currency);
    this.onChangeCallback(currency);
    this.onTouchedCallback();
    this.touch.emit();
  }

}
